import { summarizeFireCashFlow, type FireCashFlowDetail } from "@perdiem/trpc/fire-cashflow";
import { groupLabel } from "./shared";
import type { CashFlowModalData, CashFlowScheduleItem } from "./types";
import { fmtAmt } from "./shared";

// ── Budget (current-year) mapper ────────────────────────────────────────────

type BudgetCashflowResponse = {
  year: number;
  monthsElapsed: number;
  monthsRemaining: number;
  grossWageIncome: number;
  takeHomeWageIncome: number;
  passiveIncome: number;
  socialSecurityIncome: number;
  totalTakeHomeIncome: number;
  ytdActualSpend: number;
  projectedRemainingSpend: number;
  blendedAnnualExpenses: number;
  expenseCategories: Array<{
    name: string;
    group: string;
    ytdActual: number;
    projectedRemaining: number;
    blendedAnnual: number;
  }>;
  employee401k: number;
  roth401k: number;
  employer401kMatch: number;
  hsaContribution: number;
  totalRetirementContributions: number;
  annualFederalWithholding: number;
  annualStateWithholding: number;
  annualFica: number;
  estimatedTaxOwed: number | null;
  surplus: number;
  lifeEvents: Array<{
    description: string;
    type: "expense" | "inheritance" | "asset_sale";
    amount: number;
    isTaxable?: boolean | null;
    taxTreatment?:
      | "ordinary_income"
      | "short_term_capital_gain"
      | "long_term_capital_gain"
      | null;
  }>;
  contributionSchedule: {
    cash: number;
    taxable: number;
    taxDeferred: number;
    taxExempt: number;
  } | null;
  drawSchedule: {
    cash: number;
    taxable: number;
    taxDeferred: number;
    taxExempt: number;
  } | null;
};

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function groupExpenses(
  categories: BudgetCashflowResponse["expenseCategories"],
) {
  const grouped = new Map<
    string,
    { ytdActual: number; projectedRemaining: number; blendedAnnual: number }
  >();
  for (const cat of categories) {
    const label = groupLabel(cat.group);
    const existing = grouped.get(label);
    if (existing) {
      existing.ytdActual += cat.ytdActual;
      existing.projectedRemaining += cat.projectedRemaining;
      existing.blendedAnnual += cat.blendedAnnual;
    } else {
      grouped.set(label, {
        ytdActual: cat.ytdActual,
        projectedRemaining: cat.projectedRemaining,
        blendedAnnual: cat.blendedAnnual,
      });
    }
  }
  return Array.from(grouped.entries())
    .map(([name, vals]) => ({
      label: name,
      amount: vals.blendedAnnual,
      note: `${fmtAmt(vals.ytdActual)} actual + ${fmtAmt(vals.projectedRemaining)} projected`,
    }))
    .sort((a, b) => b.amount - a.amount);
}

function scheduleItems(
  schedule: { cash: number; taxable: number; taxDeferred: number; taxExempt: number },
  mode: "contribute" | "draw",
): CashFlowScheduleItem[] {
  const items: CashFlowScheduleItem[] = [];
  if (mode === "contribute") {
    if (schedule.taxExempt > 0) items.push({ label: "Roth IRA", amount: schedule.taxExempt });
    if (schedule.taxDeferred > 0) items.push({ label: "Tax-Deferred (401k/IRA)", amount: schedule.taxDeferred });
    if (schedule.taxable > 0) items.push({ label: "Taxable Brokerage", amount: schedule.taxable });
  } else {
    if (schedule.cash > 0) items.push({ label: "Cash", amount: schedule.cash });
    if (schedule.taxable > 0) items.push({ label: "Taxable Investments", amount: schedule.taxable });
    if (schedule.taxDeferred > 0) items.push({ label: "Tax-Deferred (401k/IRA)", amount: schedule.taxDeferred });
    if (schedule.taxExempt > 0) items.push({ label: "Tax-Exempt (Roth/HSA)", amount: schedule.taxExempt });
  }
  return items;
}

export function mapBudgetToCashFlowData(
  data: BudgetCashflowResponse,
): CashFlowModalData {
  // monthsElapsed is 0-indexed month count (Apr = 3), but YTD actuals include
  // the current (partial) month, so label through current month
  const ytdEnd = MONTH_NAMES[data.monthsElapsed]!;
  const projStart = MONTH_NAMES[Math.min(data.monthsElapsed + 1, 11)]!;

  const incomeItems: { label: string; amount: number }[] = [];
  if (data.takeHomeWageIncome > 0) incomeItems.push({ label: "Wage Income (take-home)", amount: data.takeHomeWageIncome });
  if (data.passiveIncome > 0) incomeItems.push({ label: "Passive Income", amount: data.passiveIncome });
  if (data.socialSecurityIncome > 0) incomeItems.push({ label: "Social Security", amount: data.socialSecurityIncome });

  const retirementSavings: CashFlowModalData["retirementSavings"] = [];
  if (data.employee401k > 0) retirementSavings.push({ label: "Employee 401(k)", amount: data.employee401k });
  if (data.roth401k > 0) retirementSavings.push({ label: "Roth 401(k)", amount: data.roth401k });
  if (data.employer401kMatch > 0) retirementSavings.push({ label: "Employer Match", amount: data.employer401kMatch, note: "Not deducted from pay" });
  if (data.hsaContribution > 0) retirementSavings.push({ label: "HSA", amount: data.hsaContribution });

  let contributionSchedule: CashFlowScheduleItem[] | undefined;
  let contributionTotal: number | undefined;
  let drawSchedule: CashFlowScheduleItem[] | undefined;
  let drawTotal: number | undefined;

  if (data.contributionSchedule) {
    contributionSchedule = scheduleItems(data.contributionSchedule, "contribute");
    contributionTotal =
      data.contributionSchedule.taxExempt +
      data.contributionSchedule.taxDeferred +
      data.contributionSchedule.taxable;
  }
  if (data.drawSchedule) {
    drawSchedule = scheduleItems(data.drawSchedule, "draw");
    drawTotal =
      data.drawSchedule.cash +
      data.drawSchedule.taxable +
      data.drawSchedule.taxDeferred +
      data.drawSchedule.taxExempt;
  }

  return {
    year: data.year,
    subtitle: `${MONTH_NAMES[0]}–${ytdEnd} actual + ${projStart}–Dec projected`,

    incomeGroups: [{ items: incomeItems }],
    incomeTotalLabel: "Total Take-Home Income",
    incomeTotal: data.totalTakeHomeIncome,

    expenseItems: groupExpenses(data.expenseCategories),
    expenseTotal: data.blendedAnnualExpenses,

    taxes: {
      type: "detailed",
      estimatedTaxOwed: data.estimatedTaxOwed,
      withholding:
        data.annualFederalWithholding > 0 ||
        data.annualStateWithholding > 0 ||
        data.annualFica > 0
          ? {
              federal: data.annualFederalWithholding,
              state: data.annualStateWithholding,
              fica: data.annualFica,
            }
          : undefined,
    },

    lifeEvents: data.lifeEvents,

    cashFlowBridge: {
      incomeAmount: data.totalTakeHomeIncome,
      expenseAmount: data.blendedAnnualExpenses,
      ...(data.lifeEvents.length > 0 && {
        lifeEventAmount: data.lifeEvents.reduce(
          (sum, e) => sum + (e.type === "expense" ? -e.amount : e.amount),
          0,
        ),
      }),
      netAmount: data.surplus,
      netLabel: data.surplus >= 0 ? "Surplus" : "Deficit",
    },

    retirementSavings: retirementSavings.length > 0 ? retirementSavings : undefined,
    retirementSavingsTotal:
      retirementSavings.length > 0
        ? data.totalRetirementContributions
        : undefined,

    contributionSchedule,
    contributionTotal,
    drawSchedule,
    drawTotal,
  };
}

// ── FIRE plan (year-detail) mapper ──────────────────────────────────────────

type FireYearDetail = FireCashFlowDetail & {
  federalTax: number;
  stateTax: number;
  taxPaid: number;
};

type LedgerEntry = {
  year: number;
  income: string | null;
  expenses: string | null;
  taxPaid: string | null;
  cash: string | null;
  taxable: string | null;
  taxDeferred: string | null;
  taxExempt: string | null;
  liquidNetWorth: string | null;
};

const WAGE_TYPES = new Set(["wages", "gig", "military"]);
const SS_TYPES = new Set(["social_security", "disability", "child_support"]);

function toN(v: string | null | undefined): number {
  return Math.round(Number(v ?? 0));
}

export function mapFirePlanToCashFlowData(
  year: number,
  detail: FireYearDetail,
  allEntries: LedgerEntry[],
  birthYear: number | null,
  agePersonName?: string | null,
  fiYear?: number | null,
  retirementNotices?: string[],
  onNavigate?: (year: number) => void,
): CashFlowModalData {
  // Income grouped by super-type
  const wages = detail.incomeStreams.filter((s) => WAGE_TYPES.has(s.type));
  const passive = detail.incomeStreams.filter(
    (s) => !WAGE_TYPES.has(s.type) && !SS_TYPES.has(s.type),
  );
  const ss = detail.incomeStreams.filter((s) => SS_TYPES.has(s.type));

  const incomeGroups: CashFlowModalData["incomeGroups"] = [];
  if (wages.length > 0)
    incomeGroups.push({
      heading: "Wages",
      items: wages.map((s) => ({ label: s.name, amount: s.annualAmount })),
    });
  if (passive.length > 0)
    incomeGroups.push({
      heading: "Passive",
      items: passive.map((s) => ({ label: s.name, amount: s.annualAmount })),
    });
  if (ss.length > 0)
    incomeGroups.push({
      heading: "Social Security",
      items: ss.map((s) => ({ label: s.name, amount: s.annualAmount })),
    });

  const incomeTotal = detail.incomeStreams.reduce((s, i) => s + i.annualAmount, 0);

  // Expenses grouped by plaidPrimary
  const expenseItems = groupFireExpenses(detail.expenseCategories);
  const expenseTotal = detail.expenseCategories.reduce((s, c) => s + c.annualAmount, 0);

  // Taxes
  const taxTotal = detail.taxPaid;
  const summary = summarizeFireCashFlow(detail, taxTotal);

  // Ledger entry for this year
  const yearIndex = allEntries.findIndex((e) => e.year === year);
  const currentEntry = yearIndex >= 0 ? allEntries[yearIndex] : null;
  const prevYear = yearIndex > 0 ? allEntries[yearIndex - 1]!.year : null;
  const nextYear =
    yearIndex >= 0 && yearIndex < allEntries.length - 1
      ? allEntries[yearIndex + 1]!.year
      : null;

  // Contribution/draw from ledger: negative = contributed, positive = drew
  const cashAmt = toN(currentEntry?.cash);
  const taxableAmt = toN(currentEntry?.taxable);
  const taxDeferredAmt = toN(currentEntry?.taxDeferred);
  const taxExemptAmt = toN(currentEntry?.taxExempt);

  // Build contribution / draw schedule from ledger
  let contributionSchedule: CashFlowScheduleItem[] | undefined;
  let contributionTotal: number | undefined;
  let drawSchedule: CashFlowScheduleItem[] | undefined;
  let drawTotal: number | undefined;

  if (currentEntry) {
    const contributions: CashFlowScheduleItem[] = [];
    const draws: CashFlowScheduleItem[] = [];

    const entries: [string, number][] = [
      ["Cash", cashAmt],
      ["Taxable Investments", taxableAmt],
      ["Tax-Deferred (401k/IRA)", taxDeferredAmt],
      ["Tax-Exempt (Roth/HSA)", taxExemptAmt],
    ];

    for (const [label, amt] of entries) {
      if (amt < 0) contributions.push({ label, amount: Math.abs(amt) });
      else if (amt > 0) draws.push({ label, amount: amt });
    }

    if (contributions.length > 0) {
      contributionSchedule = contributions;
      contributionTotal = contributions.reduce((s, c) => s + c.amount, 0);
    }
    if (draws.length > 0) {
      drawSchedule = draws;
      drawTotal = draws.reduce((s, d) => s + d.amount, 0);
    }
  }

  const age = birthYear != null ? year - birthYear : null;

  return {
    year,
    subtitle: detail.yearFraction < 1 && detail.asOf
      ? `Remaining ${year} cash flow from ${detail.asOf.slice(0, 10)}. Life events show remaining planned amounts.`
      : `Full-year projected cash flow · ${year}`,
    scheduleNote: detail.lifeEvents.some(event => event.type !== "expense")
      ? "Windfalls are added to cash before withdrawals. Cash withdrawals below can include that money."
      : undefined,
    age,
    agePersonName: agePersonName ?? null,
    isFiYear: fiYear != null && year === fiYear,
    retirementNotices,

    incomeGroups,
    incomeTotalLabel: "Total Income",
    incomeTotal,

    expenseItems,
    expenseTotal,

    taxes:
      taxTotal > 0
        ? {
            type: "simple",
            federal: detail.federalTax,
            state: detail.stateTax,
            total: taxTotal,
          }
        : null,

    lifeEvents: detail.lifeEvents,

    cashFlowBridge: {
      incomeAmount: summary.income,
      expenseAmount: summary.expenses,
      taxAmount: taxTotal,
      lifeEventAmount: summary.lifeEvents,
      netAmount: summary.net,
      netLabel: summary.net >= 0 ? "Net Savings" : "Net Deficit",
    },

    contributionSchedule,
    contributionTotal,
    drawSchedule,
    drawTotal,

    navigation: onNavigate
      ? { prevYear, nextYear, onNavigate }
      : undefined,
  };
}

function groupFireExpenses(
  categories: Array<{ name: string; group: string; annualAmount: number }>,
) {
  const grouped = new Map<string, number>();
  for (const cat of categories) {
    const label = groupLabel(cat.group);
    grouped.set(label, (grouped.get(label) ?? 0) + cat.annualAmount);
  }
  return Array.from(grouped.entries())
    .filter(([, amount]) => amount !== 0)
    .map(([name, amount]) => ({ label: name, amount }))
    .sort((a, b) => b.amount - a.amount);
}
